/* eslint-disable i18next/no-literal-string -- TanStack cache key segments */
/**
 * Workspace-scoped artifacts TanStack Query key factory.
 *
 * Lifted from `features/artifacts/api/query-keys.ts` so any surface that
 * needs the workspace-artifact list (the `artifacts` feature itself,
 * `brief-generation` intake, etc.) can consume it without crossing into
 * the `artifacts` feature slice.
 *
 * The original feature path re-exports these symbols for backward
 * compatibility — existing consumers keep working unchanged.
 *
 * Tuple shape: `['workspace', workspaceId, 'artifacts', 'list', params]`. The
 * `params` slot is preserved (even when `undefined`) so partial-match
 * invalidation via `.slice(0, N)` clears every subset:
 *
 *   list(workspaceId)        → ['workspace', workspaceId, 'artifacts', 'list', undefined]
 *   list(workspaceId, p)     → ['workspace', workspaceId, 'artifacts', 'list', p]
 *   slice(0, 4) on either    → ['workspace', workspaceId, 'artifacts', 'list']
 *   slice(0, 3) on either    → ['workspace', workspaceId, 'artifacts']
 */
import type { ArtifactKind } from '@inference.engine/widgets';

export interface WorkspaceArtifactsQueryParams {
  kind?: ArtifactKind;
  search?: string;
  sortBy?: 'name' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
  isUserInput?: boolean;
}

export const WORKSPACE_ARTIFACTS_QUERY_KEYS = {
  /** Constant tuple for invalidating EVERY workspace-artifact cache. */
  all: ['workspace', 'artifacts'] as const,

  /** Paginated/filtered artifact list under a workspace. */
  list: (workspaceId: string, params?: WorkspaceArtifactsQueryParams) =>
    ['workspace', workspaceId, 'artifacts', 'list', params] as const,
} as const;
